import BulkAction from 'components/ContactList/BulkActions/BulkAction';
import BulkActions from 'components/ContactList/BulkActions/BulkActions';
import BulkExportAction from 'components/ContactList/BulkActions/BulkExportAction';
import PageHeader from 'components/PageHeader';
import { Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import history from 'shared_history';
import moment from 'moment';
import Pagination from 'components/ContactList/Pagination';
import TotalsHeader from 'components/shared/TotalsHeader';
import { pollJob } from 'utils/jobPoller';
import { getMetaContent } from 'utils';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import withPagination from 'hocs/withPagination';
import withSorting from 'hocs/withSorting';
import { compose } from 'redux';
import { duckify, singleDuck, filterDuck } from 'utils/duckify';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';
import { formatCurrency } from 'utils';
import {
  billingConfigs,
  modals,
  schoolYears,
  fields as fieldsDuck,
  grades,
  bulkAddTransactionJobs,
  flashMessages,
  billingCategoryGroups as billingCategoriesDuck, billingAccountContacts
} from 'ducks';
import { Link, withRouter } from 'react-router';

import './styles/BillingAgingReport.scss';
import withFiltering from 'hocs/withFiltering';
import Filters from 'components/Filters/Filters';
import Placeholder from '../Placeholder';
import Edit from '../ContactList/BulkActions/Edit';
import AddCharge from '../ContactList/BulkActions/AddCharge';
import AddCredit from '../ContactList/BulkActions/AddCredit';
import useBulkActions from '../../hooks/useBulkActions';
import ContactList from '../ContactList/ContactList';
import {
  bulkCheckboxColumn,
  currencyColumn,
  defaultColumn,
  expanderColumn,
  linkColumn
} from '../ContactList/ColumnDefinitions';
import FontAwesome from '../../utils/FA';


// Load column definitions from JSON
const columns = require('../../../../config/aging_report_columns.json').map(
  (column) => {
    // By default set the column `value` to the attribute name
    let value = column.attribute;

    // Override the `name` column with a custom renderer
    if (column.type === 'name') {
      value = (account) => (
        <td className='contact-list-react-name'>
          <a href={`/contacts/${account.contact_id}/billing`}>{account[column.attribute]}</a>
        </td>
      );
    }

    return { ...column, value };
  }
);


/** Columns to display for the totals summary */
const totalColumns = columns.filter(column => column.attribute.startsWith('overdue_'));

const BillingAgingReport = ({
  contacts,
  billingCategories,
  billingConfig,
  dispatch,
  duckMeta,
  location, page,
  setPage, onSort,
  sort,
  schoolYears,
  filters,
  setFilters,
  additionalFilterFields,
  addTransaction,
  grades,
  showFlash,
  showModal,
  hideModal
}) => {
  const [ready, setReady] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [report, setReport] = useState({});

  const {
    date, rows = [], count, totals, generated_at
  } = report;

  const [currentJobId, setCurrentJobId] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [pages, setPages] = useState(1);
  const [sorting, setSorting] = useState(undefined);
  const [sortedRows, setSortedRows] = useState(rows);

  const [newDate, setNewDate] = useState(date);

  const getNewReport = (newFilters = {}, includeFilters = false, force = false) => {
    if (!ready || newDate !== date || includeFilters || force) {
      // Reuse current report date if force rerun
      const dateForReport = force ? date : newDate;
      const searchParams = { date: dateForReport };
      if (Object.keys(newFilters).length !== 0) searchParams.filters = JSON.stringify(newFilters);

      const reqSearchString = `?${queryString.stringify(searchParams)}`;

      setReportLoading(true);
      setReady(true);

      const token = getMetaContent('csrf-token');

      fetch(`/api/billing_aging_report${reqSearchString}&force=${force}`, {
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', 'X-CSRF-Token': token},
        method: 'POST'
      })
        .then(response => response.json())
        .then(jobInfo => {
          if (jobInfo.error) {
            throw new Error(jobInfo.error)
          } else {
            return pollJob(jobInfo);
          }
        })
        .then(response => {
          const newJobId = response.billing_aging_report_job.id;
          const search = { job_id: newJobId };
          const resSearchString = `?${queryString.stringify(search)}`;
          history.push({
            ...location,
            search: resSearchString
          });
        })
        .catch((error) => {
          if (error && error.message) {
            dispatch(modals.actions.show('UnexpectedError', {message: error.message}));
          } else {
            dispatch(modals.actions.show('UnexpectedError'));
          }
          setReady(false);
          setReportLoading(false);
        });
    }
  };

  const constructPagedReport = (json, search) => {
    let newPageSize = 50; // default pageSize
    setCurrentJobId(search.job_id);
    if (search.page_size) {
      newPageSize = Math.floor(Math.max(Number(search.page_size), 1));
      setPageSize(newPageSize);
    } else {
      setPageSize(50);
    }
    let newPageCount = Math.ceil(json.count / newPageSize);
    setPages(newPageCount);
    setReport(json);

    // setSortedRows to populate data
    if (search.sort !== sorting) {
      if (search.sort) {
        sortReport(json.rows, search.sort);
      } else {
        setSortedRows(json.rows);
      }
    } else {
      setSortedRows(json.rows);
    }

    setNewDate(json.date);
    if (json.job_filters) {
      setFilters(json.job_filters);
    }
    setReportLoading(false);
  };

  const removeSorting = () => {
    dispatch(modals.actions.hide());
    let search = queryString.parse(location.search);
    delete search.sort;
    const newSearchString = `?${queryString.stringify(search)}`;
    history.push({
      ...location,
      search: newSearchString
    });
  };

  const sortReport = (jsonRows, searchSort) => {
    if (searchSort === undefined) return;
    if (!searchSort) return;
    if (searchSort === "") return;

    const sortJson = JSON.parse(searchSort);
    let column = Number(sortJson.column) - 6;
    let direction = sortJson.order;

    let badSorting = false;
    const columnKey = columns[column]?.attribute;
    if (!columns[column] || columnKey === undefined || (direction !== "desc" && direction !== "asc")) badSorting = true;
    if (badSorting) {
      dispatch(modals.actions.show('UnexpectedError', {
        onClose: () => removeSorting()
      }));
    }
    const newSortedRows = [...jsonRows].sort((a, b) => {
      if (!columnKey) return 0;
      let valA = a[columnKey];
      let valB = b[columnKey];
      if (valA == null) valA = "";
      if (valB == null) valB = "";
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (!isNaN(valA) && !isNaN(valB)) {
        return (parseFloat(valA) - parseFloat(valB)) * (direction === "asc" ? 1 : -1);
      }
      return (valA < valB ? -1 : 1) * (direction === "asc" ? 1 : -1);
    });
    setSortedRows(newSortedRows);
    setSorting(searchSort);
  };

  const handleSubmitFilters = (filters) => {
    getNewReport(filters, true);
  };

  // Override default loading message for Placeholder.loading
  const loadingMessage = () => (
    <div className='placeholder-loading'>
      <FontAwesome name='spinner' spin />
      &nbsp;Your report is being prepared. It will appear here once completed. The report will also be available on the User Jobs page.
    </div>
  );

  const accountBalanceFilters = [
    { name: 'Zero Balance', id: 'zero_balance' },
    { name: 'Negative Balance', id: 'negative_balance' },
    { name: 'Positive Balance', id: 'positive_balance' }
  ];
  const [filteredSchoolYears, setYears] = useState(schoolYears);
  const filterFields = [
    {
      solr_prompt: 'school_year_ids',
      display_name: 'Term',
      options: filteredSchoolYears.map(y => [y.name, y.id])
    },
    {
      solr_prompt: 'grade',
      display_name: 'Grade',
      options: grades.map(g => [g.name, g.id])
    },
    {
      solr_prompt: 'balance',
      display_name: 'Account Balance',
      options: accountBalanceFilters.map(balanceFilter => [balanceFilter.name, balanceFilter.id])
    }
  ].concat(additionalFilterFields);
  const uniqueRows = sortedRows.filter((contact) => !contact.billing_contact_row);

  const rowsWithIndexes = uniqueRows.map(rowItem => ({ ...rowItem, id: rowItem.account_contact_id }));
  rowsWithIndexes.total = rowsWithIndexes.length; /* Used by useBulkActions */
  const pagedRowsWithIndexes = rowsWithIndexes.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const {
    trackedItems,
    selectionModeCount,
    promptSelectAll,
    selectionMode,
    togglePageTracked,
    toggleTrackedItem,
    itemIsSelected
  } = useBulkActions(pagedRowsWithIndexes);

  const fillOutColumns = (basicColumns) => [
    bulkCheckboxColumn({
      promptSelectAll,
      togglePageTracked,
      selectionMode,
      itemIsSelected,
      toggleTrackedItem,
      value: 'id',
      subRowValue: ''
    }),
    expanderColumn({}),
    linkColumn({
      ...basicColumns[0],
      value: (contact) => contact.account_last_name,
      link: (contact) => `/contacts/${contact.account_contact_id}/billing`,
      newTab: true
    }),
    linkColumn({
      ...basicColumns[1],
      value: (contact) => contact.account_first_name,
      link: (contact) => `/contacts/${contact.account_contact_id}/billing`,
      newTab: true
    }),
    linkColumn({
      ...basicColumns[2],
      value: (contact) => contact.account_middle_name,
      link: (contact) => `/contacts/${contact.account_contact_id}/billing`,
      newTab: true
    }),
    defaultColumn({
      ...basicColumns[3],
      value: '',
      subRowValue: 'billing_contact_last_name',
      columnClassNames: 'billing-contact'
    }),
    defaultColumn({
      ...basicColumns[4],
      value: '',
      subRowValue: 'billing_contact_first_name',
      columnClassNames: 'billing-contact'
    }),
    defaultColumn({
      ...basicColumns[5],
      value: '',
      subRowValue: 'billing_contact_middle_name',
      columnClassNames: 'billing-contact'
    }),
    defaultColumn({
      ...basicColumns[6],
      value: '',
      subRowValue: 'school_year'
    }),
    currencyColumn({
      ...basicColumns[7],
      defaultToZero: true,
      value: 'overdue_total'
    }),
    currencyColumn({
      ...basicColumns[8],
      defaultToZero: true,
      value: 'overdue_30'
    }),
    currencyColumn({
      ...basicColumns[9],
      defaultToZero: true,
      value: 'overdue_60'
    }),
    currencyColumn({
      ...basicColumns[10],
      defaultToZero: true,
      value: 'overdue_90'
    }),
    currencyColumn({
      ...basicColumns[11],
      defaultToZero: true,
      value: 'overdue_91_plus'
    })
  ];

  const sortContacts = (a, b) => {
    const aContactRow = a.billing_contact_id ? 1 : 0;
    const bContactRow = b.billing_contact_id ? 1 : 0;
    return aContactRow - bContactRow;
  };

  const subRowContacts = (contact) => (
    // Sort the parent row to the top so it isn't ever hanging out between multiple kids
    sortedRows.filter((row) => row.billing_contact_row && row.billing_account_id === contact.billing_account_id)
      .sort(sortContacts).map(rowItem => ({ ...rowItem, id: rowItem.billing_contact_id }))
  );

  useEffect(() => {
    const schoolYearItems = schoolYears.filter(year => year.billing_enabled);
    schoolYearItems.push({ name: 'no term', id: 'no-term' });

    setYears(schoolYearItems);
  }, [schoolYears]);

  // Queries report API when URL parameters change
  useEffect(
    () => {
      setReady(false);
      setReportLoading(false);
      let search = queryString.parse(location.search);
      if (search.job_id) {
        setReady(true);
        setReportLoading(true);
        if (search.job_id !== currentJobId) {
          fetch(`/api/billing_aging_report/${search.job_id}`, { credentials: 'include' })
            .then(response => {
              if (!response.ok) {
                throw Error(response.statusText);
              }
              return response.json();
            })
            .then(json => {
              constructPagedReport(json, search);
            })
            .catch(() => {
              dispatch(modals.actions.show('UnexpectedError'));
              setReady(false);
              setReportLoading(false);
            });
        } else if (search.sort && search.sort !== sorting) {
          sortReport(report.rows, search.sort);
          setReportLoading(false);
        } else {
          setReportLoading(false);
        }
      }
    },
    [location.search]
  );

  const columnHeaders = totalColumns.map(column => column.header);
  const columnValues = totals ? totalColumns.map(column => formatCurrency(totals[column.attribute])) : [];

  const exportLocation = {
    ...location,
    search: location.search ?
      `${location.search}&date=${encodeURIComponent(date)}` :
      `?date=${encodeURIComponent(date)}`
  };

  const downloadMessage = 'Your file is being prepared. You can safely close this modal and continue working. The '
    + 'report will automatically download when completed and will also be available on the User Jobs page.';

  const balanceDefault = { balance: ['positive_balance'] };
  return (
    <div className='billing-aging-report'>
      <div className='contact-list-react-page-header'>
        <div className='contact-list-react-page-title'>
          <PageHeader header='AR Aging Report' inline />
        </div>

        <BulkActions
          search={rowsWithIndexes.search}
          contactTotal={uniqueRows.length}
          filterType={selectionMode}
          filterContactIds={trackedItems}
          showFlash={(props) => showFlash(props)}
          showModal={(name, props) => showModal(name, props)}
          hideModal={() => hideModal()}
        >
          <BulkAction title='Export' icon={faSignOut} disabled={!ready || reportLoading}>
            <BulkExportAction
              title='Download CSV'
              dispatch={dispatch}
              location={exportLocation}
              url='/api/billing_aging_report/export.csv'
              message={downloadMessage}
            />
            <BulkExportAction
              title='Download PDF'
              dispatch={dispatch}
              location={exportLocation}
              url='/api/billing_aging_report/export.pdf'
              message={downloadMessage}
            />
          </BulkAction>

          <Edit disabled={!contacts.ready || !billingCategories.ready || selectionModeCount() === 0}>
            <AddCharge
              addTransaction={addTransaction}
              billingCategories={billingCategories}
              count={selectionModeCount()}
              jobType='account'
            />

            <AddCredit
              addTransaction={addTransaction}
              billingCategories={billingCategories}
              count={selectionModeCount()}
              jobType='account'
            />
          </Edit>
        </BulkActions>

        <p>
          Your AR Aging Report jobs will be available&nbsp;
          <Link to='/setup/user_jobs'>here</Link>
          .
        </p>
      </div>

      <div className='billing-aging-report-date'>
        Date&nbsp;
        <DatePicker
          className='billing-aging-report-input'
          isClearable
          selected={moment(newDate)
            .toDate()}
          onChange={(selectedDate) => selectedDate && setNewDate(moment(selectedDate).format('YYYY-MM-DD'))}
          name='effective_date'
          maxDate={duckMeta.billingConfig.ready && billingConfig.test_effective_date ? moment(billingConfig.test_effective_date) : moment()}
        />
      </div>

      <Placeholder.invisible ready={!ready}>
        <div className='billing-aging-report-button'>
          <Button onClick={() => getNewReport()}>Run Report</Button>
        </div>
      </Placeholder.invisible>

      <Placeholder.invisible ready={ready}>

        <div className="billing-aging-report-button-row">
          <div className="left">
            <Button disabled={!ready || reportLoading || newDate === date } onClick={() => getNewReport(filters)}>Run Report</Button>
          </div>
          <div className="right">
            <Button disabled={!ready || reportLoading} onClick={() => getNewReport(filters, false, true)}>Rerun Current
              Report</Button>
          </div>
        </div>

        <br/>
        <Placeholder.loading ready={!reportLoading}>

          <h2 className="billing-aging-report-count">
            Report generated at:{' '}{moment(generated_at)
            .format('MM/DD/YYYY hh:mm A')}
          </h2>
          <br/>
          <span className="billing-aging-report-count">
          {count}{' '}results for Effective Date:{' '}{moment(date)
            .format('MM/DD/YYYY')}{' '}{Object.keys(filters).length > 0 ? 'and selected filters' : ''}
          </span>

          <TotalsHeader columnHeaders={columnHeaders} columnValues={columnValues}/>
        </Placeholder.loading>
      </Placeholder.invisible>

      <div className='billing-aging-report-space-between'>
        {filteredSchoolYears.length > 0 && (
          <Filters
            fields={filterFields}
            selected={{...balanceDefault, ...filters}}
            submitFilters={(filters) => handleSubmitFilters(filters)}
            itemsName='terms'
            loading={reportLoading}
          />
        )}

        <Placeholder.invisible ready={ready && !reportLoading}>
          <Pagination totalPages={pages} page={page} setPage={setPage}/>
        </Placeholder.invisible>
      </div>

      <Placeholder.invisible ready={ready}>
        <Placeholder.loading ready={!reportLoading} loading={loadingMessage}>
          <ContactList
            onSort={onSort}
            sort={sort}
            columns={fillOutColumns(columns)}
            contacts={pagedRowsWithIndexes}
            emptyMessage="There are no overdue accounts."
            getSubRowContacts={subRowContacts}
          />
        </Placeholder.loading>
      </Placeholder.invisible>
    </div>
  );
};

BillingAgingReport.defaultProps = {
  addTransaction: () => false
};

const mapDuckToProps = () => ({
  billingConfig: singleDuck(billingConfigs, 'current'),
  schoolYears,
  additionalFilterFields: filterDuck(fieldsDuck, { for: 'billing' }),
  grades,
  billingCategories: filterDuck(billingCategoriesDuck, { active: true, non_contact_only: true }),
  contacts: filterDuck(billingAccountContacts)
});

const mapDispatchToProps = (dispatch) => ({
  showModal: (name, props) => dispatch(modals.actions.show(name, props)),
  addTransaction: (transaction) => dispatch(bulkAddTransactionJobs.actions.create(transaction)),
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
  hideModal: () => dispatch(modals.actions.hide())
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({ column: columns[0].sortName, order: 'asc' }),
  duckify(mapDuckToProps, () => ({}), mapDispatchToProps)
)(BillingAgingReport);
